import { defineStore } from 'pinia'
import {
  PublicStoreExpensesFormsCreate,
  PublicStoreExpensesFormsEdit,
} from '#components'
import { shiftExpenseSchema, updateShiftExpenseSchema } from '~/schemas/shiftExpenses'
import type { ShiftExpense } from '~/schemas/shiftExpenses'

export const useExpenseStore = defineStore('pos-expenses', () => {
  const log = useLogger('store:pos-expenses')
  const { get, post, put, delete: del } = useAuthenticatedFetch()

  // Get nuxtApp inside setup
  const nuxtApp = useNuxtApp()
  const { $dialog, $confirm } = nuxtApp
  const { t } = nuxtApp.$i18n

  // Get shift store with storeToRefs for reactivity
  const shiftStore = useShiftStore()
  const { businessId, shiftId } = storeToRefs(shiftStore)

  // Refs
  const expenses: Ref<any[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const expenseTypes: Ref<Array<{ value: number, label: string }>> = ref([])
  const fundSources: Ref<Array<{ value: number, label: string }>> = ref([])

  // Methods
  function reset() {
    expenses.value = []
    loading.value = false
    expenseTypes.value = []
    fundSources.value = []
    log.info('✅ Expense store reset to initial state')
  }

  async function fetchExpenseTypes(): Promise<void> {
    if (!businessId.value) return

    try {
      const response = await get(`/api/business/${businessId.value}/expenses/types`)
      expenseTypes.value = response?.select || []
    }
    catch (error) {
      log.error('Error fetching expense types:', error)
      expenseTypes.value = []
    }
  }

  async function fetchFundSources(): Promise<void> {
    if (!businessId.value) return

    try {
      const response = await get(`/api/business/${businessId.value}/expenses/fund-sources`)
      fundSources.value = response?.select || []
    }
    catch (error) {
      log.error('Error fetching fund sources:', error)
      fundSources.value = []
    }
  }

  async function fetchExpenses(shiftId: number) {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/shift/${shiftId}/expenses`)

      if (res?.success) {
        expenses.value = res?.data ?? []
        // Update shift store totals (cashExpenses drives the expected-cash calc)
        shiftStore.totalExpenses = res.total
        shiftStore.cashExpenses = res.cashTotal ?? res.total
      }
    }
    catch {
      useErrorToast('Failed to get shift transactions')
    }
    finally {
      loading.value = false
    }
  }

  async function createExpense(formData: ShiftExpense): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast(t('Business ID not found'))
      return false
    }

    // Prepare data with required fields
    const data = {
      ...formData,
      created_at: new Date(),
      business_id: businessId.value,
    }

    // Convert the new {value, label} format to string for legacy compatibility
    const expenseTypeValue = data.expense_type as any
    if (typeof expenseTypeValue === 'object' && expenseTypeValue?.label) {
      // Store both ID and string for dual compatibility
      data.expense_id = expenseTypeValue.value
      data.expense_type = expenseTypeValue.label
    }
    else if (typeof expenseTypeValue === 'number') {
      // If it's just a number (ID), find the corresponding string
      const selectedType = expenseTypes.value.find((type: any) => type.value === expenseTypeValue)
      data.expense_id = expenseTypeValue
      data.expense_type = (selectedType as any)?.label || 'Unknown'
    }

    // Validate the complete data
    const validatedData = shiftExpenseSchema.safeParse(data)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast(t('Validation failed. Please check the entered details and try again.'))
      return false
    }

    try {
      const res: { success: boolean } = await post(`/api/business/${businessId.value}/shift/${validatedData.data.shift_id}/expense`, validatedData.data)

      if (res.success) {
        useSuccessToast(t('Shift expense created successfully'))
        // Refresh expenses after creation
        await fetchExpenses(validatedData.data.shift_id)
        return true
      }
      else {
        useErrorToast(t('Failed to create shift expense'))
        return false
      }
    }
    catch (error) {
      log.error('Failed to create shift expense:', error)
      useErrorToast(t('Failed to create shift expense'))
      return false
    }
  }

  async function updateExpense(expenseId: number, formData: ShiftExpense): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast(t('Business ID not found'))
      return false
    }

    // Prepare data
    const data = {
      ...formData,
      business_id: businessId.value,
    }

    // Handle expense_id field - find the corresponding string for legacy compatibility
    if (data.expense_id) {
      const selectedType = expenseTypes.value.find((type: any) => type.value === data.expense_id)
      data.expense_type = (selectedType as any)?.label || 'Unknown'
    }

    // Remove created_at and updated_at from form data since they shouldn't be updated
    const { created_at, updated_at, ...updateData } = data

    // Validate the update data
    const validatedData = updateShiftExpenseSchema.safeParse(updateData)

    if (!validatedData.success) {
      log.error('Validation failed:', validatedData.error)
      useErrorToast(t('Validation failed. Please check the entered details and try again.'))
      return false
    }

    try {
      const res: { success: boolean } = await put(`/api/business/${businessId.value}/expense/${expenseId}`, validatedData.data)

      if (res.success) {
        useSuccessToast(t('Shift expense updated successfully'))
        // Refresh expenses after update
        if (validatedData.data.shift_id) {
          await fetchExpenses(validatedData.data.shift_id)
        }
        return true
      }
      else {
        useErrorToast(t('Failed to update shift expense'))
        return false
      }
    }
    catch (error: any) {
      log.error('Failed to update shift expense:', error)
      const errorMessage = error?.data?.message || t('Failed to update shift expense')
      useErrorToast(errorMessage)
      return false
    }
  }

  async function getExpense(expenseId: number): Promise<ShiftExpense | null> {
    if (!businessId.value) return null

    try {
      const response = await get(`/api/business/${businessId.value}/expense/${expenseId}`)
      return response?.data || null
    }
    catch (error) {
      log.error('Error fetching expense:', error)
      return null
    }
  }

  async function deleteExpense(expenseId: number): Promise<void> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return
    }

    await del(`/api/business/${businessId.value}/expense/${expenseId}`)
    useSuccessToast('Record deleted')
  }

  const deleteExpenseConfirm = (expenseId: number): void => {
    $confirm.require({
      group: 'inline',
      message: t('Do you want to delete this record?'),
      icon: 'pi pi-info-circle',
      rejectProps: {
        label: t('Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptProps: {
        label: t('Delete'),
        severity: 'danger',
      },
      accept: async () => {
        try {
          await deleteExpense(Number(expenseId))
        }
        catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
          useErrorToast(errorMessage)
        }

        if (shiftId.value) {
          await fetchExpenses(Number(shiftId.value))
        }
      },
      reject: () => {
        useErrorToast('You have rejected')
      },
    })
  }

  function createExpenseModal(shiftIdParam: string | null) {
    $dialog.open(PublicStoreExpensesFormsCreate, {
      data: {
        shiftId: shiftIdParam ? Number(shiftIdParam) : null,
        businessId: businessId.value,
      },
      props: {
        header: t('Add new expense'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        if (shiftIdParam) {
          await fetchExpenses(Number(shiftIdParam))
        }
      },
    })
  }

  function editExpenseModal(expenseId: number) {
    if (!shiftId.value) {
      useErrorToast('Shift ID not found')
      return
    }

    $dialog.open(PublicStoreExpensesFormsEdit, {
      data: {
        shiftId: Number(shiftId.value),
        expenseId: Number(expenseId),
        businessId: businessId.value,
      },
      props: {
        header: t('Edit shift expense'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        if (shiftId.value) {
          await fetchExpenses(Number(shiftId.value))
        }
      },
    })
  }

  return {
    expenses,
    loading,
    expenseTypes,
    fundSources,
    fetchExpenses,
    fetchExpenseTypes,
    fetchFundSources,
    createExpense,
    updateExpense,
    getExpense,
    deleteExpense,
    deleteExpenseConfirm,
    createExpenseModal,
    editExpenseModal,
    reset,
  }
}, {
  persist: {
    key: 'pos-expense-store',
    storage: localStorage,
    paths: ['expenses'], // Only persist expenses data
  },
})
